import React, { useEffect, useState, useRef } from "react";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Snackbar,
  Backdrop,
  Divider,
  Grid,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { API_HOST, API_URL_WS } from "../common/common";

// Component to handle map view animation
const MapViewHandler = ({ devices }) => {
  const map = useMap();
  const initialized = useRef(false);

  useEffect(() => {
    if (devices.length > 0 && !initialized.current) {
      const points = devices.map((device) => [
        device.latitude,
        device.longitude,
      ]);
      const bounds = L.latLngBounds(points);
      map.fitBounds(bounds, { padding: [50, 50], animate: true });
      initialized.current = true;
    }
  }, [devices, map]);

  return null;
};

const Map = () => {
  const location = useLocation();
  const [devices, setDevices] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [loadingState, setLoadingState] = useState("connecting"); // connecting, connected, error
  const wsRef = useRef(null);

  // Use theme and media query for responsive layout
  const theme = useTheme();
  const isDesktopOrTablet = useMediaQuery(theme.breakpoints.up("sm"));

  // Clean up function for websocket
  const cleanupWebSocket = () => {
    if (wsRef.current) {
      wsRef.current.onopen = null;
      wsRef.current.onmessage = null;
      wsRef.current.onclose = null;
      wsRef.current.onerror = null;

      if (
        wsRef.current.readyState === WebSocket.OPEN ||
        wsRef.current.readyState === WebSocket.CONNECTING
      ) {
        wsRef.current.close();
      }
      wsRef.current = null;
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get("t");

    if (!t) {
      setErrorMessage(
        'La URL no contiene el parámetro "t" con el token de acceso.'
      );
      setLoadingState("error");
      return;
    }

    const wsUrl = `${API_URL_WS}guest?t=${t}`;

    setLoadingState("connecting");
    console.log("Intentando conectar al WebSocket...");

    wsRef.current = new WebSocket(wsUrl);

    wsRef.current.onopen = () => {
      console.log("Conectado al servidor WebSocket");
      setLoadingState("connected");
      wsRef.current.send("Hello Server!");
    };

    wsRef.current.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.devices && Array.isArray(data.devices)) {
          setDevices(data.devices);
        }
      } catch (err) {
        console.error("Error al procesar mensaje:", err);
      }
    };

    wsRef.current.onclose = (event) => {
      if (event.wasClean) {
        console.log("Conexión WebSocket cerrada limpiamente.");
        setErrorMessage("El token de acceso ha expirado.");
      } else {
        console.error("Conexión WebSocket cerrada abruptamente.");
        setErrorMessage(
          "El token de acceso ha expirado o es inválido, gracias por usar nuestro servicio."
        );
      }

      setLoadingState("error");
    };

    wsRef.current.onerror = () => {
      console.error("Error en WebSocket");
      setErrorMessage(
        "El token ha expirado o es inválido. La conexión no pudo establecerse."
      );
      setLoadingState("error");
    };

    // Cleanup function
    return () => {
      console.log("Limpiando componente Map");
      cleanupWebSocket();
    };
  }, [location.search]); // Only re-run if the URL search params change

  const customIcon = (iconUrl) => {
    return L.icon({
      iconUrl: iconUrl,
      iconSize: [38, 38],
    });
  };

  // Handle error message dismissal
  const handleCloseError = () => {};

  // Styles for map and info container
  const containerStyle = {
    height: "100vh",
    width: "100%",
    overflow: "hidden",
    display: "flex",
    flexDirection: isDesktopOrTablet ? "row" : "column",
    backgroundColor: "#f0f2f5",
  };

  return (
    <Box sx={containerStyle}>
      {/* Map container */}
      <Box
        sx={{
          flex: isDesktopOrTablet ? 2 : 2,
          position: "relative",
          height: isDesktopOrTablet ? "100%" : "50%",
        }}
      >
        <Box sx={{ height: "100%", width: "100%", position: "relative" }}>
          <MapContainer
            center={[-9.92998, -76.2422]}
            zoom={14}
            style={{ height: "100%", width: "100%" }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            {devices.map((device) => (
              <Marker
                key={device.name}
                position={[device.latitude, device.longitude]}
                icon={customIcon(
                  device?.icon
                    ? `${API_HOST}${device.icon}`
                    : "/images/coche.png"
                )}
              />
            ))}
            <MapViewHandler devices={devices} />
          </MapContainer>
        </Box>
      </Box>

      {/* Information panel */}
      <Box
        sx={{
          flex: 1,
          height: isDesktopOrTablet ? "100%" : "50%",
          backgroundColor: "#ffffff",
          boxShadow: isDesktopOrTablet
            ? "-4px 0 10px rgba(0,0,0,0.1)"
            : "0 -4px 10px rgba(0,0,0,0.1)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            p: 2,
            bgcolor: "#1976d2",
            color: "white",
            boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            Información del Vehículo
          </Typography>
        </Box>

        <Box sx={{ p: 0, overflow: "auto", flexGrow: 1 }}>
          {loadingState === "connecting" && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                p: 3,
              }}
            >
              <CircularProgress size={40} sx={{ mb: 2, color: "#1976d2" }} />
              <Typography variant="body1" sx={{ color: "#555" }}>
                Conectando al servicio...
              </Typography>
            </Box>
          )}

          {loadingState === "connected" && devices.length === 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                p: 3,
              }}
            >
              <Typography variant="body1" sx={{ color: "#555" }}>
                No se encontraron dispositivos
              </Typography>
            </Box>
          )}

          {loadingState === "connected" && devices.length > 0 && (
            <Box>
              {devices.map((device, index) => (
                <React.Fragment key={device.name}>
                  <Box sx={{ p: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: "bold", color: "#1976d2", mb: 2 }}
                    >
                      {device.name}
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            p: 1.5,
                            bgcolor: "#f6f9fc",
                            borderRadius: 1,
                            border: "1px solid #e0e7ff",
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="#666"
                            sx={{ mb: 0.5 }}
                          >
                            Velocidad
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 500, color: "#333" }}
                          >
                            {device.speed} km/h
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            p: 1.5,
                            bgcolor: "#f6f9fc",
                            borderRadius: 1,
                            border: "1px solid #e0e7ff",
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="#666"
                            sx={{ mb: 0.5 }}
                          >
                            Curso
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 500, color: "#333" }}
                          >
                            {device.course}°
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            p: 1.5,
                            bgcolor: "#f6f9fc",
                            borderRadius: 1,
                            border: "1px solid #e0e7ff",
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="#666"
                            sx={{ mb: 0.5 }}
                          >
                            Conductor
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 500, color: "#333" }}
                          >
                            {device.driver || "No especificado"}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            p: 1.5,
                            bgcolor: "#f6f9fc",
                            borderRadius: 1,
                            border: "1px solid #e0e7ff",
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="#666"
                            sx={{ mb: 0.5 }}
                          >
                            Teléfono
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 500, color: "#333" }}
                          >
                            {device.phone || "No especificado"}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            p: 1.5,
                            bgcolor: "#f6f9fc",
                            borderRadius: 1,
                            border: "1px solid #e0e7ff",
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="#666"
                            sx={{ mb: 0.5 }}
                          >
                            Estado
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 500, color: "#333" }}
                          >
                            {device.status || "No especificado"}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            p: 1.5,
                            bgcolor: "#f6f9fc",
                            borderRadius: 1,
                            border: "1px solid #e0e7ff",
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="#666"
                            sx={{ mb: 0.5 }}
                          >
                            IMEI
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 500, color: "#333" }}
                          >
                            {device.uniqueid || "No especificado"}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                  {index < devices.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {/* Error message as a snackbar */}
      <Snackbar
        open={!!errorMessage && loadingState !== "error"}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={6000}
      >
        <Box
          sx={{
            bgcolor: "error.dark",
            color: "white",
            py: 2,
            px: 3,
            borderRadius: 1,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}
        >
          <Typography variant="body1">{errorMessage}</Typography>
        </Box>
      </Snackbar>

      {/* Error overlay with semi-transparent backdrop */}
      <Backdrop
        sx={{ color: "#fff", zIndex: 1000 }}
        open={loadingState === "error"}
        onClick={handleCloseError}
      >
        <Box
          sx={{
            p: 4,
            bgcolor: "error.main",
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            maxWidth: "80%",
            boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
          }}
        >
          <Typography
            variant="h5"
            component="h2"
            align="center"
            color="white"
            sx={{ mb: 2 }}
          >
            Error de Conexión
          </Typography>
          <img
            src="/images/advertencia.png"
            alt="Error"
            width={64}
            height={64}
            style={{ marginBottom: 16 }}
          />
          <Typography variant="body1" align="center" color="white">
            {errorMessage}
          </Typography>
        </Box>
      </Backdrop>
    </Box>
  );
};

export default Map;
